import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { analytics } from '@/analytics';
import BioscopeDetailMovingBioscopeModal from '@/components/molecules/BioscopeDetailMovingBioscopeModal/BioscopeDetailMovingBioscopeModal';
import { ModalBody, ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { usePermissions } from '@/contexts/auth';
import {
    type MoveBioscopeQueryParams,
    useGetBioscopeDetail,
    useMoveBioscope,
} from '@/domain/bioscopes';
import { useGetAllClientsWithLocations } from '@/domain/client';
import { now, startOf } from '@/utils/date';

export interface BioscopeMovingModalContainerProps {
    /** Undefined until a bioscope is picked; the modal stays empty until its detail loads. */
    bioscopeId: number | undefined;
    isOpen: boolean;
    onClose: () => void;
    onMoveSuccess: () => void;
}
const showRelocationSuccessToast = (t: TFunction) => {
    ToastSuccess(t('organisms.moveBioscopeModal.relocationRegistered'), 'relocation-success-toast');
};

export function BioscopeMovingModalContainer({
    bioscopeId,
    isOpen,
    onClose,
    onMoveSuccess,
}: BioscopeMovingModalContainerProps) {
    const { t } = useTranslation();
    const { isOptoScaleAdmin, hasFullClientAccess } = usePermissions();
    // 0 only fills the query key while there is no bioscope; the query does not run then.
    const bioscopeQuery = useGetBioscopeDetail(bioscopeId ?? 0, {
        enabled: bioscopeId !== undefined,
    });
    const { data: bioscope, refetch: refetchBioscopeDetail } = bioscopeQuery;
    const updateBioscopeLocation = useMoveBioscope({
        onSuccess: () => {
            analytics.featureActionUsed({ featureName: 'moving_bioscopes' });
            refetchBioscopeDetail();
            showRelocationSuccessToast(t);
            onMoveSuccess();
        },
    });
    const clientsQuery = useGetAllClientsWithLocations();
    const { data: clientWithLocations } = clientsQuery;
    const currentClientId = bioscope?.clientId ?? 0;

    const clientsWithAuthorizedLocations = clientWithLocations ?? [];
    const currentClient = clientsWithAuthorizedLocations.find(
        (client) => client.id === currentClientId
    );
    const hasMultipleAuthorizedLocations = (currentClient?.locations.length ?? 0) > 1;
    const hasClientWideAccess = hasFullClientAccess(currentClientId);

    const onSubmit = async (data: MoveBioscopeQueryParams) => {
        if (!bioscope) return;
        await updateBioscopeLocation.mutateAsync({ bioscopeId: bioscope.id, move: data });
        refetchBioscopeDetail();
    };

    // The form needs both; without one the dialog used to stay shut, so Move did nothing.
    if (isOpen && bioscopeId !== undefined && (bioscopeQuery.error || clientsQuery.error)) {
        return (
            <ModalComponent
                isOpen
                onClose={onClose}
                centered
                title={`${t('common.actions.move')} ${t('common.labels.bioscope')} ${bioscopeId}`}>
                <ModalBody>
                    <SomethingHappened
                        title={t('organisms.moveBioscopeModal.loadError')}
                        {...retryFailedQueries(bioscopeQuery, clientsQuery)}
                    />
                </ModalBody>
            </ModalComponent>
        );
    }

    return (
        bioscope &&
        clientWithLocations &&
        isOpen && (
            <BioscopeDetailMovingBioscopeModal
                isModalOpenMoveBioscope={isOpen}
                clients={clientsWithAuthorizedLocations}
                initalState={{
                    bioscopeId: bioscope.id,
                    clientId: bioscope.clientId,
                    contractId: bioscope.contractId,
                    locationId: bioscope.lastLocation?.cage?.location?.id,
                    cageId: bioscope.lastLocation?.cage?.id,
                    from: startOf(now(), 'hour'),
                }}
                onCloseModal={onClose}
                onSubmit={onSubmit}
                canModifyClient={isOptoScaleAdmin}
                canModifyContract={isOptoScaleAdmin}
                canModifyLocation={hasClientWideAccess || hasMultipleAuthorizedLocations}
                canMoveToLand={hasClientWideAccess}
            />
        )
    );
}
